import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import ImpersonationBanner from '../../components/ImpersonationBanner'
import { EmptyState, ErrorState, LoadingState, mutedTextClass, Notice, pageTitleClass } from '../../components/ui'
import { api } from '../../lib/api'
import { formatDate } from '../../lib/dateFormat'
import { daysBetween, todayIso } from '../../lib/roster/dates'
import { localizeRosterRefusal } from '../../lib/roster/rosterRefusal'
import { NOW_DOWNTIME, NOW_FREE, NOW_LEAVE, NOW_TRAVEL } from '../../lib/roster/derive'
import { buildLeaveIndex, leaveOn } from '../../lib/roster/leave'
import { groupOwnRuns, OWN_AHEAD_DAYS, ownNow, ownRuns, ownWindow, type OwnRun } from '../../lib/roster/ownRuns'
import { buildProjectGrid } from '../../lib/roster/projectGrid'
import { OUT_OF_SCOPE } from '../../lib/roster/types'
import { useProjectRoster } from '../../lib/rosterApi'
import type { User } from '../../lib/types'
import { labelMap, labelOf } from '../../lib/roster/labels'
import { useOptionalProjectLabel } from '../../lib/roster/useProjectLabel'
import type { ProjectNames } from '../../lib/rosterProjectNames'

interface OwnScheduleDay {
  day: string
  plan: string
  planBase: string
  actual: string
  codes: string[]
  source: 'guest-log' | 'roster' | null
  project: string
  lodge: boolean
  meals: string
  comment: string
}

interface OwnScheduleCode {
  code: string
  label: string
  colour: string
  cat: string
  project: string
}

export interface OwnSchedule {
  person: {
    id: string
    name: string
    role: string
    company: string
    home: string
    kind: string
    has_left: boolean
  } | null
  from: string
  to: string
  codes: OwnScheduleCode[]
  days: OwnScheduleDay[]
  /** Optional because an older server omits it. */
  projectNames?: Record<string, ProjectNames>
  /** Own days from today on whose Draft differs from the Plan shown here. */
  draftChanges?: number
}

export function mealLabelKeys(marks: string): string[] {
  const byMark: Record<string, string> = { B: 'roster.mealB', L: 'roster.mealL', D: 'roster.mealD' }
  return [...marks].map((m) => byMark[m]).filter((k): k is string => Boolean(k))
}

/** A code missing from the legend (best-effort, can be `[]`) falls back to the raw slug: visibly unlabelled beats silently dropped. */
export function codeLabels(codes: string[], legend: Map<string, OwnScheduleCode>): string[] {
  return codes.map((c) => legend.get(c)?.label || c)
}

interface ScheduleDayCodes {
  codes: string[]
  /** `plan` is a fallback, but the caller must show it as planned, never as worked: a sentence must not assert a fact nobody filed. */
  planned: boolean
}

export function scheduleDayCodes(day: Pick<OwnScheduleDay, 'codes' | 'plan'>): ScheduleDayCodes {
  if (day.codes.length > 0) return { codes: day.codes, planned: false }
  const planned = day.plan.split('+').filter(Boolean)
  return { codes: planned, planned: planned.length > 0 }
}

function useOwnSchedule(today: string) {
  const { from, to } = ownWindow(today)
  return useQuery({
    queryKey: ['roster', 'me', from, to],
    queryFn: async () => (await api.get<OwnSchedule>('/api/roster/me', { params: { from, to } })).data,
    retry: false,
  })
}

const RUN_GRID = 'grid gap-2 sm:grid-cols-2 xl:grid-cols-3'

function dayLabel(iso: string, locale: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return formatDate(new Date(Date.UTC(y!, m! - 1, d, 12)), locale, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

export default function MySchedule({ user, embedded = false }: { user: User; embedded?: boolean }) {
  const { t } = useTranslation()
  const today = todayIso()
  const { data, isLoading, isError, error, refetch, isRefetching } = useOwnSchedule(today)
  const groups = useMemo(() => (data ? groupOwnRuns(ownRuns(data), today) : null), [data, today])
  const names = data?.projectNames

  const Root = embedded ? 'div' : 'main'
  const body = (
      <Root className={`w-full flex-1 space-y-4 ${embedded ? '' : 'p-4 sm:p-6'}`}>
        <h1 className={embedded ? 'sr-only' : pageTitleClass}>{t('roster.mySchedule.title')}</h1>

        {isError && (
          <ErrorState
            region
            title={t('roster.mySchedule.loadFailed')}
            error={error}
            localizeDetail={(detail) => localizeRosterRefusal(detail, t)}
            onRetry={() => void refetch()}
            retrying={isRefetching}
          />
        )}
        {isLoading && <LoadingState region label={t('action.loading')} />}

        {data && (data.draftChanges ?? 0) > 0 && (
          <Notice>{t('roster.mySchedule.draftPending', { count: data.draftChanges })}</Notice>
        )}

        {data && data.person === null && <EmptyState region title={t('roster.mySchedule.notOnRoster')} />}
        {data && data.person !== null && data.days.length === 0 && (
          <EmptyState region title={t('roster.mySchedule.nothingAhead', { count: OWN_AHEAD_DAYS })} />
        )}

        {data && groups && data.days.length > 0 && (
          <>
            <section className="space-y-2" aria-label={t('roster.mySchedule.horizon', { count: OWN_AHEAD_DAYS })}>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('roster.mySchedule.horizon', { count: OWN_AHEAD_DAYS })}
              </h2>
              <ol className={RUN_GRID}>
                <RightNow value={ownNow(data, today)} run={groups.current} today={today} names={names} />
                {groups.ahead.map((run) => (
                  <RunCard key={run.from} run={run} today={today} names={names} />
                ))}
              </ol>
              {groups.ahead.length === 0 && (
                <p className={mutedTextClass}>{t('roster.mySchedule.nothingAhead', { count: OWN_AHEAD_DAYS })}</p>
              )}
            </section>

            {groups.earlier.length > 0 && (
              <details className="space-y-2">
                <summary className="flex min-h-10 cursor-pointer items-center text-sm font-semibold text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus dark:text-slate-100">
                  {t('roster.mySchedule.earlier')}
                </summary>
                <ol className={RUN_GRID}>
                  {groups.earlier.map((run) => (
                    <RunCard key={run.from} run={run} today={today} names={names} />
                  ))}
                </ol>
              </details>
            )}
          </>
        )}

        <MyProject />
      </Root>
  )
  if (embedded) return body
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-900">
      <ImpersonationBanner user={user} />
      {body}
    </div>
  )
}

/** No editors and no actuals: the endpoint cannot carry them. */
function MyProject() {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useProjectRoster()
  const projectText = useOptionalProjectLabel(data?.projectNames)
  if (isLoading) return null
  if (isError || !data) return null
  const grid = buildProjectGrid(data)
  const labels = labelMap(data.people)
  const leave = buildLeaveIndex(data.leave ?? [])
  const cellClass = 'whitespace-nowrap border-l border-slate-100 px-1.5 py-1 text-center text-[11px] dark:border-slate-700'

  return (
    <section className="space-y-2" aria-label={t('roster.myProjectTitle')}>
      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {t('roster.myProjectTitle')}
        {data.projects.length > 0 && (
          <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
            {data.projects.map(projectText).join(', ')}
          </span>
        )}
      </h2>
      {data.projects.length === 0 || grid.rows.length === 0 ? (
        <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          {t('roster.myProjectNone')}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 dark:text-slate-400">
                  <th className="sticky left-0 bg-white px-2 py-1 text-left font-medium dark:bg-slate-800">
                    {data.from} – {data.to}
                  </th>
                  {grid.days.map((d) => (
                    <th key={d} className={`${cellClass} font-normal tabular-nums`}>
                      {d.slice(8)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row) => (
                  <tr key={row.name} className="border-t border-slate-100 dark:border-slate-700">
                    <th className="sticky left-0 whitespace-nowrap bg-white px-2 py-1 text-left font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                      {labelOf(labels, row.name)}
                      {row.role && (
                        <span className="ml-1 font-normal text-slate-500 dark:text-slate-400">
                          {row.role}
                        </span>
                      )}
                    </th>
                    {row.cells.map((cell, i) => {
                      const colour = cell && !cell.elsewhere ? legendColour(grid, cell.codes[0]) : ''
                      const lv = leaveOn(leave, row.name, grid.days[i] ?? '')
                      return (
                        <td
                          key={grid.days[i]}
                          className={`${cellClass} ${
                            cell?.elsewhere
                              ? 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200'
                              : colour
                                ? 'text-slate-800'
                                : 'text-slate-700 dark:text-slate-200'
                          }`}
                          style={{
                            ...(colour ? { backgroundColor: colour } : {}),
                            ...(lv
                              ? {
                                  backgroundImage:
                                    lv.state === 'pending'
                                      ? 'repeating-linear-gradient(90deg, rgba(124,58,237,0.38) 0 2px, rgba(0,0,0,0) 2px 5px)'
                                      : lv.halfDay
                                        ? 'linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(124,58,237,0.30) 50%)'
                                        : 'linear-gradient(rgba(124,58,237,0.30), rgba(124,58,237,0.30))',
                                }
                              : {}),
                          }}
                          title={
                            [
                              cell?.elsewhere ? t('roster.myProjectElsewhere') : null,
                              lv
                                ? [
                                    lv.state === 'pending'
                                      ? t('roster.leavePending')
                                      : t('roster.leaveApproved'),
                                    lv.halfDay ? t('roster.leaveHalf') : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')
                                : null,
                            ]
                              .filter(Boolean)
                              .join(String.fromCharCode(10)) || undefined
                          }
                        >
                          {cell ? (cell.elsewhere ? '~' : cell.codes.join('+')) : ''}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600 dark:text-slate-300">
            {grid.legend.map((l) => (
              <li key={l.code} className="inline-flex items-center gap-1">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-sm ${
                    l.code === OUT_OF_SCOPE ? 'bg-slate-300 dark:bg-slate-500' : ''
                  }`}
                  style={l.colour ? { backgroundColor: l.colour } : undefined}
                />
                {l.code}{' '}
                {l.code === OUT_OF_SCOPE ? t('roster.myProjectElsewhere') : l.label !== l.code && l.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function legendColour(grid: ReturnType<typeof buildProjectGrid>, code: string): string {
  return grid.legend.find((l) => l.code === code)?.colour ?? ''
}

/** The code's own colour as a bar, not a fill: a full pastel wash under text costs more contrast than it gives recognition. */
function RunBar({ run }: { run: OwnRun | undefined }) {
  const colours = (run?.codes ?? []).map((c) => c.colour).filter(Boolean)
  return (
    <div className="flex w-1.5 shrink-0 flex-col overflow-hidden rounded-full">
      {colours.length === 0 ? (
        <span className="flex-1 bg-slate-200 dark:bg-slate-600" />
      ) : (
        colours.map((colour, i) => <span key={i} className="flex-1" style={{ backgroundColor: colour }} />)
      )}
    </div>
  )
}

/** Reads `ownNow`, the desk grid's Now rule, so the two cannot disagree. */
function RightNow({
  value,
  run,
  today,
  names,
}: {
  value: string
  run: OwnRun | undefined
  today: string
  names?: Record<string, ProjectNames>
}) {
  const { t } = useTranslation()
  const projectText = useOptionalProjectLabel(names)
  const label =
    value === NOW_FREE
      ? t('roster.nowFree')
      : value === NOW_TRAVEL
        ? t('roster.nowTravel')
        : value === NOW_LEAVE
          ? t('roster.nowLeave')
          : value === NOW_DOWNTIME
            ? t('roster.nowDowntime')
            : projectText(value)

  return (
    <li className="flex gap-3 rounded-xl border border-blue-300 bg-white p-3 dark:border-blue-800 dark:bg-slate-800">
      <RunBar run={run} />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('roster.mySchedule.rightNow')}</p>
        <p className="break-words text-lg font-semibold text-slate-900 dark:text-slate-100">{label}</p>
        {run && <RunBody run={run} today={today} names={names} hideProject={value} />}
      </div>
    </li>
  )
}

function RunCard({ run, today, names }: { run: OwnRun; today: string; names?: Record<string, ProjectNames> }) {
  return (
    <li className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      <RunBar run={run} />
      <div className="min-w-0 flex-1">
        <RunBody run={run} today={today} names={names} />
      </div>
    </li>
  )
}

const CHIP = 'rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-700 dark:text-slate-300'

function RunBody({
  run,
  today,
  names,
  hideProject,
}: {
  run: OwnRun
  today: string
  names?: Record<string, ProjectNames>
  hideProject?: string
}) {
  const { t, i18n } = useTranslation()
  const projectText = useOptionalProjectLabel(names)
  const locale = i18n.language
  const dates =
    run.from === run.to ? dayLabel(run.from, locale) : `${dayLabel(run.from, locale)} – ${dayLabel(run.to, locale)}`
  const projects = run.projects.filter((p) => p !== hideProject)
  const starting = run.from > today ? daysBetween(today, run.from) : 0
  const current = run.from <= today && today <= run.to

  return (
    <>
      <p className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{dates}</p>
      <p className="break-words text-sm font-medium text-slate-800 dark:text-slate-100">
        {run.codes.length > 0 ? run.codes.map((c) => c.label).join(' + ') : t('roster.mySchedule.free')}
      </p>
      {projects.length > 0 && (
        <p className="break-words text-xs text-slate-600 dark:text-slate-300">{projects.map(projectText).join(', ')}</p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
        {!run.cut && run.days > 1 && (
          <span className={CHIP}>
            {current
              ? t('roster.mySchedule.dayOf', { n: daysBetween(run.from, today) + 1, total: run.days })
              : t('roster.mySchedule.days', { count: run.days })}
          </span>
        )}
        {run.source === 'actual' && (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
            {t('roster.mySchedule.filed')}
          </span>
        )}
        {run.source === 'plan' && (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
            {t('roster.mySchedule.planned')}
          </span>
        )}
        {starting > 0 && (
          <span className="text-slate-500 dark:text-slate-400">
            {starting === 1 ? t('roster.mySchedule.startsTomorrow') : t('roster.mySchedule.startsIn', { count: starting })}
          </span>
        )}
      </div>
      <RunNotes run={run} locale={locale} />
    </>
  )
}

/** A one-day run shows its note outright; a longer one folds them, so a filed fortnight stays one card. */
function RunNotes({ run, locale }: { run: OwnRun; locale: string }) {
  const { t } = useTranslation()
  if (run.notes.length === 0) return null
  const rows = (
    <ul className="mt-1 space-y-1 text-[11px]">
      {run.notes.map((day) => (
        <li key={day.day} className="flex flex-wrap items-center gap-1.5">
          {run.days > 1 && <span className="tabular-nums text-slate-500 dark:text-slate-400">{dayLabel(day.day, locale)}</span>}
          {mealLabelKeys(day.meals).map((key) => (
            <span key={key} className={CHIP}>
              {t(key)}
            </span>
          ))}
          {day.lodge && <span className={CHIP}>{t('roster.lodge')}</span>}
          {day.comment && <span className="break-words text-slate-500 dark:text-slate-400">{day.comment}</span>}
        </li>
      ))}
    </ul>
  )
  if (run.days === 1) return rows
  return (
    <details className="mt-1">
      <summary className="flex min-h-10 cursor-pointer items-center text-[11px] text-slate-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus dark:text-slate-300">
        {t('roster.mySchedule.dayNotes', { count: run.notes.length })}
      </summary>
      {rows}
    </details>
  )
}
